import { get } from './api.service';
import {
  DashboardSummary,
  RabbitScoreResult,
  ClientRabbitScore,
  RiskMetrics,
  PerformanceTrend,
  ScripPerformance,
  DealDistribution,
  Client,
  Trade,
  Alert,
  PortfolioSummary,
} from '../types';

interface DashboardResponse {
  summary: DashboardSummary;
}

interface ClientDashboardResponse {
  client: Client;
  portfolio: PortfolioSummary;
  openPositions: Trade[];
  recentClosed: Trade[];
  alerts: Alert[];
}

interface RabbitScoreResponse {
  rabbitScore: RabbitScoreResult;
  breakdown: string;
  totalTrades: number;
}

interface ClientRabbitScoresResponse {
  clientScores: ClientRabbitScore[];
}

interface RiskResponse {
  riskMetrics: RiskMetrics;
}

interface TrendsResponse {
  trends: PerformanceTrend[];
}

interface ScripPerformanceResponse {
  scripPerformance: ScripPerformance[];
}

interface DealDistributionResponse {
  distribution: DealDistribution[];
  dealCounts: {
    positive: number;
    negative: number;
    range: number;
    total: number;
  };
  totalDeals: number;
}

// ============================================================================
// ANALYTICS SERVICE
// ============================================================================

export const analyticsService = {
  /**
   * Get main dashboard summary with RABBIT SCORE
   */
  async getDashboardSummary(): Promise<DashboardSummary> {
    const response = await get<DashboardResponse>('/dashboard/summary');
    return response.summary;
  },

  /**
   * Get dashboard for specific client
   */
  async getClientDashboard(clientId: string): Promise<ClientDashboardResponse> {
    const response = await get<ClientDashboardResponse>(`/dashboard/client/${clientId}`);
    return response;
  },

  /**
   * Get risk dashboard data
   */
  async getRiskDashboard(): Promise<RiskMetrics> {
    const response = await get<RiskResponse>('/dashboard/risk');
    return response.riskMetrics;
  },

  /**
   * Get portfolio-wide RABBIT SCORE
   */
  async getPortfolioRabbitScore(): Promise<RabbitScoreResponse> {
    const response = await get<RabbitScoreResponse>('/analytics/rabbit-score');
    return response;
  },

  /**
   * Get RABBIT SCORE breakdown by client
   */
  async getClientRabbitScores(): Promise<ClientRabbitScore[]> {
    const response = await get<ClientRabbitScoresResponse>('/analytics/rabbit-score/clients');
    return response.clientScores;
  },

  /**
   * Get performance trends over time
   */
  async getPerformanceTrends(period = 30): Promise<PerformanceTrend[]> {
    const response = await get<TrendsResponse>('/analytics/performance-trends', { period });
    return response.trends;
  },

  /**
   * Get scrip/symbol performance
   */
  async getScripPerformance(): Promise<ScripPerformance[]> {
    const response = await get<ScripPerformanceResponse>('/analytics/scrip-performance');
    return response.scripPerformance;
  },

  /**
   * Get deal distribution for charts
   */
  async getDealDistribution(): Promise<DealDistributionResponse> {
    const response = await get<DealDistributionResponse>('/analytics/deal-distribution');
    return response;
  },
};

export default analyticsService;
